import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@state/core.state';
import { catchError, exhaustMap, map, mergeMap, of, forkJoin } from 'rxjs';
import { distinctUntilChanged, withLatestFrom, take } from 'rxjs/operators';

import {
	RestService,
	ProjectCarrierMapDTO,
	ProjectGroup,
	IS_NOT_NULL_OR_UNDEFINED,
	DELAY_OPTIONS,
	IS_NULL_OR_UNDEFINED,
} from 'cargo-chief-commons';

import { PROJECT_GROUPS, PROJECT_CARRIER_MAP } from '../actions/';
import { projectDetailsPageStateKey, ProjectDetailsState } from '../state';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { DEFAULT_SYSTEM_GROUP_NAME } from '@domain/constants/project-types';
import {
	ALLOCATED_CARRIERS_GROUP_NAME,
	ALLOCATED_CARRIERS_GROUP_DELAY,
} from '@service/project-group/project-group.service';

import { ProjectGroupDialogComponent } from '../../project-group-dialog/project-group-dialog.component';
import { ProjectDetailsEffects } from '../effects/project-details.effects';

@Injectable({
	providedIn: 'root',
})
export class ProjectGroupEffects {
	constructor(
		private actions$: Actions<any>,
		private readonly store: Store<State>,
		private readonly restService: RestService,
		private readonly urlService: UrlService,
		public utilService: UtilService,
		private dialog: MatDialog,
		public readonly projectDetailsEffects: ProjectDetailsEffects
	) {}

	openProjectGroupDialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.OPEN_DIALOG),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			exhaustMap((request) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let dialogData = Object.assign({}, request[0]);

				// Count user groups excluding system groups AND the Allocated Carriers group
			// This ensures new groups get numbered correctly (e.g., Priority 2 after Priority 1)
			let totalNumberOfProjectGroups = !projectDetailsPageState.projectGroups.length
					? 1
					: projectDetailsPageState.projectGroups.filter(
						(group) => !group.systemGroup && group.name !== ALLOCATED_CARRIERS_GROUP_NAME
					).length + 1;

				dialogData['totalNumberOfProjectGroups'] = totalNumberOfProjectGroups;
				dialogData['delayByMinutesList'] = projectDetailsPageState.projectGroups.map(
					(group) => group.delayByMinutes
				);
				dialogData['groupsCount'] = projectDetailsPageState.projectGroups.filter(
					(group) => !group.systemGroup
				).length;
				dialogData['groupNames'] = projectDetailsPageState.projectGroups.map((group) =>
					group.name.toLowerCase()
				);
				if (!projectDetailsPageState.projectGroups.length) {
					let options = [].concat(DELAY_OPTIONS);
					options.shift();
					dialogData['delayByMinutesList'] = options.map((delay) => delay.value);
				}
				let dialogRef = this.dialog.open(ProjectGroupDialogComponent, {
					width: '550px',
					panelClass: 'cargo_chief_dialog_container',
					data: dialogData,
				});
				return dialogRef.afterClosed();
			}),
			map((result: any) => {
				if (result) {
					this.store
						.select((state) => state[projectDetailsPageStateKey])
						.pipe(take(1), distinctUntilChanged())
						.subscribe((state) => {
							const isProjectGroupExist = state.projectGroups.length > 0;
							if (isProjectGroupExist) {
								this.store.dispatch(PROJECT_GROUPS.CREATE(result));
							} else {
								this.store.dispatch(PROJECT_GROUPS.GROUP_AND_DEFAULT_GROUP.CREATE(result));
							}
						});
				}
				return PROJECT_GROUPS.CLOSE_DIALOG({});
			})
		)
	);

	createProjectGroups$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.CREATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let requestDTO = Object.assign({}, request[0]);
				delete requestDTO.type;
				return this.restService.POST(this.urlService.createProjectGroups(), requestDTO).pipe(
					map((response: any) => {
						if (response.data) {
							const projectCarrierMapIds = response.data.projectCarrierMapDataList.map(
								(pcm) => pcm.projectCarrierMapId
							);
							// check if any group is empty
							let deleteProjectGroupIds = [];
							if (projectDetailsPageState.projectGroups.length) {
								projectDetailsPageState.projectGroups.forEach((group) => {
									const groupPcmIds = group.projectCarrierMapsList.map((pcm) => pcm.id);
									const allGroupCarriersSelected = groupPcmIds.every((pcmId) =>
										projectCarrierMapIds.includes(pcmId)
									);
									if (allGroupCarriersSelected) {
										deleteProjectGroupIds.push(group.id);
									}
								});
							}

							let projectGroup: ProjectGroup = response.data;
							let allProjectCarrierMapsList = [];
							projectDetailsPageState.allProjectCarrierMapsList.forEach((pcm) => {
								if (projectCarrierMapIds.indexOf(pcm.id) === -1) {
									allProjectCarrierMapsList.push(pcm);
								} else {
									let carrierMap = Object.assign({}, pcm);
									carrierMap.projectGroup = Object.assign({}, projectGroup);
									allProjectCarrierMapsList.push(carrierMap);
								}
							});
							this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
							this.projectDetailsEffects.setProjectGroups(allProjectCarrierMapsList);
							this.store.dispatch(
								PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
									list: allProjectCarrierMapsList,
								})
							);

							// Check if there's an "Allocated Carriers" group that needs its delay updated
							// to stay as the lowest priority (highest delay) group
							const allocatedCarriersGroup = projectDetailsPageState.projectGroups.find(
								(group) => group.name === ALLOCATED_CARRIERS_GROUP_NAME
							);
							const newGroupDelay = projectGroup.delayByMinutes;

							if (allocatedCarriersGroup && allocatedCarriersGroup.delayByMinutes <= newGroupDelay) {
								// Update Allocated Carriers group to have higher delay than the new group
								const newAllocatedDelay = newGroupDelay + ALLOCATED_CARRIERS_GROUP_DELAY;
								this.store.dispatch(
									PROJECT_GROUPS.UPDATE({
										projectCarrierMapIdList: allocatedCarriersGroup.projectCarrierMapsList.map(
											(pcm) => pcm.id
										),
										projectId: projectDetailsPageState.projectId,
										projectGroupId: allocatedCarriersGroup.id,
										delayByMinutes: newAllocatedDelay,
									})
								);
							}

							// delete empty groups
							if (deleteProjectGroupIds.length) {
								this.store.dispatch(
									PROJECT_GROUPS.DELETE({
										ids: deleteProjectGroupIds,
									})
								);
							} else {
								// get remaining carriers in systemGroup and update it
								const systemGroupPcmIds: string[] = projectDetailsPageState.projectGroups
									.find((group) => group.systemGroup)
									?.projectCarrierMapsList.map((pcm) => pcm.id)
									.filter((pcmId) => projectCarrierMapIds.indexOf(pcmId) === -1);
								if (systemGroupPcmIds?.length) {
									this.projectDetailsEffects.createOrUpdateSystemGroup(systemGroupPcmIds);
								}
							}
						}
						this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
						return PROJECT_GROUPS.CREATE_SUCCESS({});
					}),
					catchError((error) => {
						this.utilService.showSnackbar(
							'Unable to create project group. Please try again',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
						return of(PROJECT_GROUPS.CREATE_FAILURE({ payload: { message: error.error } }));
					})
				);
			})
		)
	);

	deleteProjectGroups$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.DELETE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let requestDTO = Object.assign({}, request[0]);
				const moveToGroupId = requestDTO.moveToGroupId;
				const skipRemainingGroupsCheck = requestDTO.skipRemainingGroupsCheck;
				delete requestDTO.type;
				delete requestDTO.moveToGroupId;
				delete requestDTO.skipRemainingGroupsCheck;
				// if only system group is left, delete that also
				const systemGroup: ProjectGroup = projectDetailsPageState.projectGroups.find(
						(group) => group.systemGroup && requestDTO.ids.indexOf(group.id) === -1
					),
					projectGroupsCount = projectDetailsPageState.projectGroups.length - requestDTO.ids.length;
				let projectCarrierMapIdsOfDeletedGroups = [];
				if (IS_NULL_OR_UNDEFINED(skipRemainingGroupsCheck)) {
					if (projectGroupsCount === 1 && systemGroup) {
						requestDTO.ids = requestDTO.ids.concat(systemGroup.id);
					} else {
						const selectedProjectGroups = projectDetailsPageState.projectGroups.filter(
							(group) => requestDTO.ids.indexOf(group.id) !== -1
						);
						selectedProjectGroups.forEach((group) => {
							projectCarrierMapIdsOfDeletedGroups = projectCarrierMapIdsOfDeletedGroups.concat(
								group.projectCarrierMapsList.map((pcm) => pcm.id)
							);
						});
					}
				}

				return this.restService.DELETE(this.urlService.deleteProjectGroups(), requestDTO).pipe(
					map((response: any) => {
						const deletedGroups: ProjectGroup[] = projectDetailsPageState.projectGroups
							.filter((group) => requestDTO.ids.indexOf(group.id) !== -1)
							.map((group) => Object.assign({}, group));
						let removedCarriersList: ProjectCarrierMapDTO[] = [];
						deletedGroups.forEach((group) => {
							removedCarriersList = removedCarriersList.concat(
								group.projectCarrierMapsList.map((pcm) => Object.assign({}, pcm))
							);
						});
						let allProjectCarrierMapsList: ProjectCarrierMapDTO[] = [];
						const deletedGroupsPcmIds = removedCarriersList.map((pcm) => pcm.id);
						projectDetailsPageState.allProjectCarrierMapsList.forEach((pcm) => {
							if (deletedGroupsPcmIds.indexOf(pcm.id) === -1) {
								allProjectCarrierMapsList.push(pcm);
							} else {
								let carrierMap = Object.assign({}, pcm);
								carrierMap.projectGroup = null;
								allProjectCarrierMapsList.push(carrierMap);
							}
						});

						this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
						this.projectDetailsEffects.setProjectGroups(allProjectCarrierMapsList);
						this.store.dispatch(
							PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
								list: allProjectCarrierMapsList,
							})
						);

						if (moveToGroupId) {
							const moveToGroup = projectDetailsPageState.projectGroups.find(
								(group) => group.id === moveToGroupId
							);
							this.store.dispatch(
								PROJECT_GROUPS.UPDATE({
									projectCarrierMapIdList: moveToGroup.projectCarrierMapsList
										.map((pcm) => pcm.id)
										.concat(projectCarrierMapIdsOfDeletedGroups),
									projectId: projectDetailsPageState.projectId,
									projectGroupId: moveToGroup.id,
								})
							);
						} else if (projectCarrierMapIdsOfDeletedGroups.length) {
							// add carrier maps to system group if exist or else create system group
							this.store
								.select((state) => state[projectDetailsPageStateKey])
								.pipe(take(1))
								.subscribe((state) => {
									const projectGroupsCount: number = state.projectGroups.filter(
										(group) => !group.systemGroup
									).length;
									if (projectGroupsCount) {
										this.projectDetailsEffects.createOrUpdateSystemGroup(deletedGroupsPcmIds);
									}
								});
						}

						return PROJECT_GROUPS.DELETE_SUCCESS({});
					}),
					catchError((error) => {
						this.utilService.showSnackbar(
							'Unable to delete project group. Please try again',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
						return of(PROJECT_GROUPS.DELETE_FAILURE({ payload: { message: error.error } }));
					})
				);
			})
		)
	);

	deleteCarrierFromProjectGroups$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.DELETE_CARRIER),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let requestDTO = Object.assign({}, request[0]),
					projectGroupUpdateRequest = { projectGroups: [] };

				// select group from which carriers are selected
				projectDetailsPageState.projectGroups.forEach((group) => {
					let projectCarrierMapIdList: string[] = group.projectCarrierMapsList
						.filter((pcm) => requestDTO.projectCarrierMapIds.indexOf(pcm.id) !== -1)
						.map((pcm) => pcm.id);
					if (projectCarrierMapIdList.length) {
						// add new carriers in group list if any carrier from group is selected
						projectGroupUpdateRequest.projectGroups.push({
							projectGroupId: group.id,
							projectCarrierMapIdList: group.projectCarrierMapsList
								.filter((pcm) => requestDTO.projectCarrierMapIds.indexOf(pcm.id) === -1)
								.map((pcm) => pcm.id),
						});
					}
				});

				// add all the removed carriers to system group if exist
				let systemGroup: ProjectGroup = projectDetailsPageState.projectGroups.find(
					(group) => group.systemGroup
				);
				if (systemGroup) {
					projectGroupUpdateRequest.projectGroups.push({
						projectGroupId: systemGroup.id,
						projectCarrierMapIdList: requestDTO.projectCarrierMapIds.concat(
							systemGroup.projectCarrierMapsList.map((pcm) => pcm.id)
						),
					});
				}
				return this.restService
					.PUT(
						this.urlService.updateProjectGroup(
							projectGroupUpdateRequest.projectGroups[0].projectGroupId
						),
						projectGroupUpdateRequest
					)
					.pipe(
						map((response: any) => {
							// set project group to system group for selected carriers
							let allProjectCarrierMapsList = [];
							if (systemGroup) {
								projectDetailsPageState.allProjectCarrierMapsList.forEach((pcm) => {
									if (requestDTO.projectCarrierMapIds.indexOf(pcm.id) === -1) {
										allProjectCarrierMapsList.push(pcm);
									} else {
										let carrierMap = Object.assign({}, pcm);
										carrierMap.projectGroup = systemGroup ? systemGroup : null;
										allProjectCarrierMapsList.push(carrierMap);
									}
								});
							} else {
								allProjectCarrierMapsList = projectDetailsPageState.allProjectCarrierMapsList;
							}

							// remove
							this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
							this.projectDetailsEffects.setProjectGroups(allProjectCarrierMapsList);
							this.store.dispatch(
								PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
									list: allProjectCarrierMapsList,
								})
							);
							// if only system group is left delete it
							this.store
								.select((state) => state[projectDetailsPageStateKey])
								.pipe(take(1))
								.subscribe((state) => {
									const projectGroupsCount = state.projectGroups.length;
									if (projectGroupsCount === 1 && state.projectGroups[0].systemGroup) {
										this.store.dispatch(
											PROJECT_GROUPS.DELETE({
												ids: [state.projectGroups[0].id],
											})
										);
									} else if (!systemGroup) {
										this.projectDetailsEffects.createOrUpdateSystemGroup(
											requestDTO.projectCarrierMapIds
										);
									}
								});

							return PROJECT_GROUPS.DELETE_CARRIER_SUCCESS({});
						}),
						catchError((error) => {
							this.utilService.showSnackbar(
								'Unable to delete project group. Please try again',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of(
								PROJECT_GROUPS.DELETE_CARRIER_FAILURE({
									payload: { message: error.error },
								})
							);
						})
					);
			})
		)
	);

	moveProjectGroups$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.MOVE_GROUPS),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let requestDTO = Object.assign({}, request[0]);
				delete requestDTO.type;
				let projectGroups = [...projectDetailsPageState.projectGroups],
					selectedProjectGroups = projectGroups.filter(
						(group) => requestDTO.groupIds.indexOf(group.id) !== -1
					),
					projectGroupUpdateRequest = { projectGroups: [] },
					projectGroupsToUpdate = [];

				if (requestDTO.direction === 'up') {
					const targetCarrierIndex: number =
						projectDetailsPageState.projectGroups.findIndex(
							(group) => requestDTO.groupIds.indexOf(group.id) !== -1
						) - 1;
					if (targetCarrierIndex < 0) {
						this.utilService.showSnackbar(
							'Cannot move above top priority group',
							SNACKBAR.WARNING,
							SNACKBAR.ACTION
						);
						return of(PROJECT_GROUPS.MOVE_GROUPS_SUCCESS({}));
					}
					let priorityToSwap: number =
						projectDetailsPageState.projectGroups[targetCarrierIndex].delayByMinutes;

					projectGroupsToUpdate = [projectDetailsPageState.projectGroups[targetCarrierIndex]]
						.concat(projectGroups.filter((group) => requestDTO.groupIds.indexOf(group.id) !== -1))
						.map((group) => Object.assign({}, group));
					projectGroupsToUpdate.forEach((group, index) => {
						if (index === 0) {
							group.delayByMinutes =
								projectGroupsToUpdate[projectGroupsToUpdate.length - 1].delayByMinutes;
						} else {
							const tempPriority = group.delayByMinutes;
							group.delayByMinutes = priorityToSwap;
							priorityToSwap = tempPriority;
						}
					});
					projectGroupsToUpdate.forEach((group) => {
						projectGroupUpdateRequest.projectGroups.push({
							projectGroupId: group.id,
							projectCarrierMapIdList: group.projectCarrierMapsList.map((pcm) => pcm.id),
							delayByMinutes: group.delayByMinutes,
						});
					});
				} else {
					let lowestPriorityIndex = 0;
					projectDetailsPageState.projectGroups.forEach((group, index) => {
						if (requestDTO.groupIds.indexOf(group.id) !== -1) {
							if (index > lowestPriorityIndex) {
								lowestPriorityIndex = index;
							}
						}
					});
					lowestPriorityIndex += 1;
					if (lowestPriorityIndex + 1 > projectDetailsPageState.projectGroups.length) {
						this.utilService.showSnackbar(
							'Cannot move below lowest priority group',
							SNACKBAR.WARNING,
							SNACKBAR.ACTION
						);
						return of(PROJECT_GROUPS.MOVE_GROUPS_SUCCESS({}));
					}

					projectGroupsToUpdate = projectGroups
						.filter((group) => requestDTO.groupIds.indexOf(group.id) !== -1)
						.concat([projectDetailsPageState.projectGroups[lowestPriorityIndex]])
						.map((group) => Object.assign({}, group));
					let highestPriorityToSwap: number = projectGroupsToUpdate[0].delayByMinutes;
					projectGroupsToUpdate.forEach((group, index) => {
						if (index === projectGroupsToUpdate.length - 1) {
							group.delayByMinutes = highestPriorityToSwap;
						} else {
							group.delayByMinutes = projectGroupsToUpdate[index + 1].delayByMinutes;
						}
					});
					projectGroupsToUpdate.forEach((group) => {
						projectGroupUpdateRequest.projectGroups.push({
							projectGroupId: group.id,
							projectCarrierMapIdList: group.projectCarrierMapsList.map((pcm) => pcm.id),
							delayByMinutes: group.delayByMinutes,
						});
					});
				}
				return this.restService
					.PUT(
						this.urlService.updateProjectGroup(
							projectGroupUpdateRequest.projectGroups[0].projectGroupId
						),
						projectGroupUpdateRequest
					)
					.pipe(
						map((response: any) => {
							this.utilService.showSnackbar(
								'Project groups updated',
								SNACKBAR.SUCCESS,
								SNACKBAR.ACTION
							);
							let updatedProjectGroupIds: string[] = [];
							if (response.data) {
								updatedProjectGroupIds = response.data.map((group) => group.id);
							}
							let finalProjectGroupsList = projectGroupsToUpdate
								.filter((group) => updatedProjectGroupIds.indexOf(group.id) !== -1)
								.concat(
									projectDetailsPageState.projectGroups.filter(
										(group) => updatedProjectGroupIds.indexOf(group.id) === -1
									)
								)
								.sort((a, b) => {
									// "Allocated Carriers" group always goes last, regardless of delayByMinutes
									if (a.name === ALLOCATED_CARRIERS_GROUP_NAME) return 1;
									if (b.name === ALLOCATED_CARRIERS_GROUP_NAME) return -1;
									// All other groups sort by delayByMinutes
									return a.delayByMinutes > b.delayByMinutes ? 1 : -1;
								});
							this.store.dispatch(
								PROJECT_GROUPS.SET.PROJECT_GROUPS({
									list: finalProjectGroupsList,
								})
							);
							return PROJECT_GROUPS.MOVE_GROUPS_SUCCESS({});
						}),
						catchError((error) => {
							this.utilService.showSnackbar(
								'Unable to update project groups. Please try again',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of(
								PROJECT_GROUPS.MOVE_GROUPS_FAILURE({
									payload: { message: error.error },
								})
							);
						})
					);
			})
		)
	);

	createGroupAndDefaultGroup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.GROUP_AND_DEFAULT_GROUP.CREATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let requestDTO = Object.assign({}, request[0]);
				delete requestDTO.type;
				const systemGroupRequest = {
					systemGroup: true,
					projectCarrierMapIdList: projectDetailsPageState.procuredCarriersList
						.filter((pcm) => requestDTO.projectCarrierMapIdList.indexOf(pcm.id) === -1)
						.map((pcm) => pcm.id),
					name: DEFAULT_SYSTEM_GROUP_NAME,
					projectId: projectDetailsPageState.projectId,
					delayByMinutes: [].concat(DELAY_OPTIONS).sort((a, b) => (a.value > b.value ? -1 : 1))[0]
						.value,
				};
				return this.createGroupAndDefaultGroup(
					requestDTO,
					systemGroupRequest,
					projectDetailsPageState.allProjectCarrierMapsList
				);
			})
		)
	);

	async createGroupAndDefaultGroup(
		createGroupRequest,
		systemGroupRequest,
		allProjectCarrierMapsList
	) {
		const createGroupResponse = await this.createGroups(createGroupRequest, systemGroupRequest);
		let projectCarrierMapsList = allProjectCarrierMapsList.map((pcm) => Object.assign({}, pcm));
		projectCarrierMapsList.forEach((pcm) => {
			if (createGroupRequest.projectCarrierMapIdList.indexOf(pcm.id) !== -1) {
				pcm.projectGroup = Object.assign({}, createGroupResponse[0].data);
			}
			if (systemGroupRequest.projectCarrierMapIdList.indexOf(pcm.id) !== -1) {
				pcm.projectGroup = Object.assign({}, createGroupResponse[1].data);
			}
		});
		this.projectDetailsEffects.setProcuredCarriers(projectCarrierMapsList);
		this.projectDetailsEffects.setProjectGroups(projectCarrierMapsList);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
				list: projectCarrierMapsList,
			})
		);
		return PROJECT_GROUPS.GROUP_AND_DEFAULT_GROUP.SUCCESS({});
	}

	async createGroups(createGroupRequest, systemGroupRequest) {
		const createGroup = this.restService
				.POST(this.urlService.createProjectGroups(), createGroupRequest)
				.pipe(
					catchError((error) => {
						this.utilService.showSnackbar(
							'Unable to create group.',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
						return of({});
					})
				),
			createSystemGroup = this.restService
				.POST(this.urlService.createProjectGroups(), systemGroupRequest)
				.pipe(
					catchError((error) => {
						this.utilService.showSnackbar(
							'Unable to create system group.',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
						return of({});
					})
				);
		let requests = [createGroup];
		if (systemGroupRequest.projectCarrierMapIdList.length) {
			requests.push(createSystemGroup);
		}
		return await forkJoin(...requests).toPromise();
	}

	updateProjectGroup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.UPDATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let requestDTO = Object.assign({}, request[0]);
				delete requestDTO.type;
				return this.restService
					.PUT(this.urlService.updateProjectGroup(requestDTO.projectGroupId), {
						projectGroups: [requestDTO],
					})
					.pipe(
						map((response: any) => {
							if (response.data) {
								this.updateProjectCarrierMapsAndGroups(
									response,
									projectDetailsPageState.allProjectCarrierMapsList
								);
							}
							return PROJECT_GROUPS.CREATE_SUCCESS({});
						}),
						catchError((error) => {
							this.utilService.showSnackbar(
								'Unable to update project group. Please try again',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of(PROJECT_GROUPS.CREATE_FAILURE({ payload: { message: error.error } }));
						})
					);
			})
		)
	);

	addToGroups$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.ADD_TO_GROUP.ADD),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let requestDTO = Object.assign({}, request[0]);
				delete requestDTO.type;
				const projectGroupUpdateRequest = requestDTO.updateGroups.projectGroups;

				return this.restService
					.PUT(this.urlService.updateProjectGroup(projectGroupUpdateRequest[0].projectGroupId), {
						projectGroups: projectGroupUpdateRequest,
					})
					.pipe(
						map((response: any) => {
							this.updateProjectCarrierMapsAndGroups(
								response,
								projectDetailsPageState.allProjectCarrierMapsList
							);
							if (requestDTO.deleteGroupIds?.length) {
								this.store.dispatch(
									PROJECT_GROUPS.DELETE({
										ids: requestDTO.deleteGroupIds,
										skipRemainingGroupsCheck: true,
									})
								);
							}
							return PROJECT_GROUPS.CREATE_SUCCESS({});
						}),
						catchError((error) => {
							this.utilService.showSnackbar(
								'Unable to add to project group.',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of(PROJECT_GROUPS.ADD_TO_GROUP.FAILURE({ payload: { message: error.error } }));
						})
					);
			})
		)
	);

	updateProjectCarrierMapsAndGroups(
		projectGroupsUpdateResponse: any,
		allProjectCarrierMapsListInStore: ProjectCarrierMapDTO[]
	) {
		let projectCarrierMapIds = [];
		projectGroupsUpdateResponse.data.forEach((group) => {
			projectCarrierMapIds = projectCarrierMapIds.concat(
				group.projectCarrierMapDataList.map((pcm) => pcm.projectCarrierMapId)
			);
		});
		let allProjectCarrierMapsList = [];
		allProjectCarrierMapsListInStore.forEach((pcm) => {
			if (projectCarrierMapIds.indexOf(pcm.id) === -1) {
				allProjectCarrierMapsList.push(pcm);
			} else {
				let carrierMap = Object.assign({}, pcm);
				let projectGroup: ProjectGroup = projectGroupsUpdateResponse.data.find((group) =>
					IS_NOT_NULL_OR_UNDEFINED(
						group.projectCarrierMapDataList.find((data) => data.projectCarrierMapId === pcm.id)
					)
				);
				if (projectGroup) {
					carrierMap.projectGroup = Object.assign({}, projectGroup);
				}
				allProjectCarrierMapsList.push(carrierMap);
			}
		});
		this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
		this.projectDetailsEffects.setProjectGroups(allProjectCarrierMapsList);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
				list: allProjectCarrierMapsList,
			})
		);
		this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
	}

	updateGroupDialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_GROUPS.UPDATE_GROUP.UPDATE),
			exhaustMap((data) => {
				let dialogRef = this.dialog.open(ProjectGroupDialogComponent, {
					width: '550px',
					panelClass: 'cargo_chief_dialog_container',
					data: data,
				});
				return dialogRef.afterClosed();
			}),
			mergeMap((result: any) => {
				if (result) {
					return of(PROJECT_GROUPS.UPDATE(result));
				}
				return of(PROJECT_GROUPS.UPDATE_GROUP.CANCEL({}));
			})
		)
	);
}
